import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import {
  contactKeys,
  estimateKeys,
  expenseKeys,
  invoiceKeys,
  membershipKeys,
  pricebookKeys,
  settingsKeys,
} from '@/lib/queryKeys';
import type {
  AccountingConflictDiff,
  AccountingConflictsList,
  ConflictResolutionAction,
} from '@/types/accounting-sync';

export function useAccountingConflicts(filter?: {
  entityType?: string;
  limit?: number;
  cursor?: string | null;
}) {
  const search = new URLSearchParams();
  if (filter?.entityType) search.set('entityType', filter.entityType);
  if (filter?.limit != null) search.set('limit', String(filter.limit));
  if (filter?.cursor) search.set('cursor', filter.cursor);
  const qs = search.toString();
  const url = settingsEndpoints.accountingSyncConflicts() + (qs ? `?${qs}` : '');

  return useQuery({
    queryKey: [
      ...settingsKeys.accountingConflicts(),
      filter?.entityType ?? 'all',
      filter?.limit ?? 'no-limit',
      filter?.cursor ?? 'first',
    ],
    queryFn: () => apiGet<AccountingConflictsList>(operationsClient, url),
    // Webhook-driven, so a fresh conflict can land at any time without a
    // client mutation — poll every 10s while the operator is on this tab.
    staleTime: 0,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Side-by-side diff for a single conflict. Lazy — only fires when the
 * caller passes `enabled: true` (the row is expanded). Stays cached so
 * collapsing and re-expanding doesn't refetch.
 */
export function useAccountingConflictDiff(logId: string, enabled: boolean) {
  return useQuery({
    queryKey: settingsKeys.accountingConflictDiff(logId),
    queryFn: () =>
      apiGet<AccountingConflictDiff>(
        operationsClient,
        settingsEndpoints.accountingSyncConflictDiff(logId),
      ),
    enabled,
    staleTime: 30_000,
  });
}

// Map the resolved entity type to the list/detail cache namespaces that
// must refetch so list pages reflect the new data after resolution.
//
// Payments / deposits / credit memos / change orders are typically nested
// under a contact's detail view, so contactKeys.all() covers them too. We
// invalidate the union to keep the UI honest no matter where the operator
// lands.
const ENTITY_KEY_NAMESPACES: Record<string, Array<() => readonly unknown[]>> = {
  contact: [contactKeys.all],
  invoice: [invoiceKeys.all],
  estimate: [estimateKeys.all],
  item: [pricebookKeys.all],
  expense: [expenseKeys.all],
  membership_charge: [membershipKeys.all],
  // No standalone list namespaces for these — they're rendered inside the
  // contact detail. Refetching contactKeys.all() picks up the embedded data.
  payment: [contactKeys.all],
  deposit: [contactKeys.all],
  credit_memo: [contactKeys.all],
  change_order: [contactKeys.all],
};

export function useResolveAccountingConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      logId: string;
      action: ConflictResolutionAction;
      // Used for cache invalidation after resolve.
      entityType: string;
      entityId: string;
    }) =>
      apiPost<
        { resolved: boolean; action: ConflictResolutionAction },
        { action: ConflictResolutionAction }
      >(operationsClient, settingsEndpoints.accountingSyncResolveConflict(input.logId), {
        action: input.action,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: settingsKeys.accountingConflicts() });
      qc.invalidateQueries({ queryKey: settingsKeys.accountingHistory() });
      qc.invalidateQueries({ queryKey: settingsKeys.accountingDashboard() });
      // The resolution path also writes a SUCCESS log row, which can clear a
      // queued failure for the same entity — refetch failures so the count
      // doesn't go stale.
      qc.invalidateQueries({ queryKey: settingsKeys.accountingFailures() });
      qc.invalidateQueries({
        queryKey: settingsKeys.accountingEntity(vars.entityType, vars.entityId),
      });
      for (const ns of ENTITY_KEY_NAMESPACES[vars.entityType] ?? []) {
        qc.invalidateQueries({ queryKey: ns() });
      }
      toast.success(
        vars.action === 'accept_qb'
          ? 'Pulling QuickBooks data — local row will overwrite shortly'
          : 'Pushing Notifi data — QuickBooks will overwrite shortly',
      );
    },
  });
}
